/**
 * DataTable Component
 * Reusable table component
 */

import React from 'react';
import styled from 'styled-components';

const TableContainer = styled.div`
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: var(--bg-secondary);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const TableHeader = styled.th`
  padding: var(--spacing-md);
  text-align: left;
  font-size: var(--font-sm);
  font-weight: var(--font-semibold);
  color: var(--text-secondary);
`;

const TableCell = styled.td`
  padding: var(--spacing-md);
  font-size: var(--font-md);
  color: var(--text-primary);
`;

const EmptyState = styled.div`
  padding: var(--spacing-xl);
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--font-md);
`;

interface Column {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
}

interface DataTableProps {
  columns: Column[];
  data: any[];
  emptyMessage?: string;
  onRowClick?: (row: any) => void;
}

const DataTable: React.FC<DataTableProps> = ({ columns, data, emptyMessage = 'No data available', onRowClick }) => {
  if (data.length === 0) {
    return (
      <TableContainer>
        <EmptyState>{emptyMessage}</EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableHeader key={column.key}>{column.label}</TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <tbody>
          {data.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              onClick={() => onRowClick && onRowClick(row)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {columns.map((column) => (
                <TableCell key={column.key}>
                  {column.render ? column.render(row[column.key], row) : row[column.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;
